/**
 * BusinessShowerFlyerModal - Form for creating Business Shower flyers
 */

import React, { useState } from 'react';
import { jsPDF } from 'jspdf';
import './CustomFlyerModal.css';

function BusinessShowerFlyerModal({ isOpen, onClose }) {
  const [formData, setFormData] = useState({
    guestName: '',
    location: '',
    address: '',
    date: '',
    time: '',
    theme: '',
    headshotPicture: null
  });
  const [headshotPreview, setHeadshotPreview] = useState(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData(prev => ({
        ...prev,
        headshotPicture: file
      }));
      
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setHeadshotPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const generateFlyer = async () => {
    try {
      // Create PDF in portrait orientation
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });

      // Load the business shower template image
      const imgPath = '/images/business-shower.jpeg';
      
      const img = new Image();
      img.crossOrigin = 'anonymous';
      
      img.onload = async function() {
        // Add template image as background
        const pageWidth = pdf.internal.pageSize.getWidth();
        const pageHeight = pdf.internal.pageSize.getHeight();
        pdf.addImage(img, 'JPEG', 0, 0, pageWidth, pageHeight);

        // Add customizable text on top
        pdf.setTextColor(255, 200, 0); // Gold/yellow text to match design
        pdf.setFont('helvetica', 'bold');

        // Guest Name (personalized greeting)
        if (formData.guestName) {
          pdf.setFontSize(14);
          pdf.text(`${formData.guestName}`, (pageWidth / 2) + 10, 35, { align: 'center' });
        }

        // Date
        if (formData.date) {
          pdf.setFontSize(14);
          pdf.text(formData.date, 40, 115);
        }

        // Time
        if (formData.time) {
          pdf.setFontSize(14);
          pdf.text(formData.time, pageWidth - 40, 115, { align: 'right' });
        }

        // Location/Address
        if (formData.location) {
          pdf.setTextColor(255, 200, 0);
          pdf.setFont('helvetica', 'normal');
          pdf.setFontSize(14);
          const locLines = pdf.splitTextToSize(formData.location, 100);
          pdf.text(locLines, pageWidth - 50, 125, { align: 'right' });
        }

        // Address (second line)
        if (formData.address) {
          pdf.setTextColor(255, 200, 0);
          pdf.setFont('helvetica', 'normal');
          pdf.setFontSize(14);
          const addressLines = formData.address.split(' ').filter(Boolean);
          pdf.text(addressLines, pageWidth - 50, 130, { align: 'right' });
        }

        // Theme
        // if (formData.theme) {
        //   pdf.setTextColor(255, 255, 255); // White for theme
        //   pdf.setFont('helvetica', 'normal');
        //   pdf.setFontSize(10);
        //   const themeLines = pdf.splitTextToSize(formData.theme, 150);
        //   pdf.text(themeLines, pageWidth / 2, 185, { align: 'center' });
        // }

        // Add headshot if provided
        if (headshotPreview) {
          try {
            const headshotImg = new Image();
            headshotImg.src = headshotPreview;
            await new Promise((resolve) => {
              headshotImg.onload = resolve;
            });
            
            // Add headshot in center area (matching template position)
            const headshotSize = 45;
            const headshotX = (pageWidth - headshotSize) / 2;
            const headshotY = 120;
            
            pdf.addImage(headshotImg, 'JPEG', headshotX, headshotY, headshotSize, headshotSize);
          } catch (error) {
            console.error('Error adding headshot:', error);
          }
        }

        // Download the PDF
        const fileName = `Business-Shower-${formData.guestName || 'Flyer'}.pdf`;
        pdf.save(fileName);

        // Reset form and close
        setFormData({
          guestName: '',
          location: '',
          address: '',
          date: '',
          time: '',
          theme: '',
          headshotPicture: null
        });
        setHeadshotPreview(null);
        onClose();
      };

      img.onerror = function() {
        alert('Error loading business shower image. Please ensure business-shower.jpeg exists in public/images/');
      };

      img.src = imgPath;

    } catch (error) {
      console.error('Error generating flyer:', error);
      alert('Error generating flyer. Please try again.');
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    generateFlyer();
  };

  if (!isOpen) return null;

  return (
    <div className="custom-flyer-modal-overlay" onClick={onClose}>
      <div className="custom-flyer-modal" onClick={(e) => e.stopPropagation()}>
        <div className="custom-flyer-header">
          <h2 className="custom-flyer-title">Business Shower & Venture Kickoff Flyer</h2>
          <button 
            className="custom-flyer-close"
            onClick={onClose}
            aria-label="Close modal"
          >
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} className="custom-flyer-form">
          {/* Guest Name Input */}
          <div className="custom-flyer-field">
            <label htmlFor="guestName" className="custom-flyer-label-text">Guest Name:</label>
            <input
              type="text"
              id="guestName"
              name="guestName"
              value={formData.guestName}
              onChange={handleInputChange}
              placeholder="Enter guest name"
              className="custom-flyer-input custom-flyer-input-simple"
              required
            />
          </div>

          {/* Date Input */}
          <div className="custom-flyer-field">
            <label htmlFor="date" className="custom-flyer-label-text">Date:</label>
            <input
              type="text"
              id="date"
              name="date"
              value={formData.date}
              onChange={handleInputChange}
              placeholder="mm/dd/yyyy"
              className="custom-flyer-input custom-flyer-input-simple"
            />
          </div>

          {/* Time Input */}
          <div className="custom-flyer-field">
            <label htmlFor="time" className="custom-flyer-label-text">Time:</label>
            <input
              type="text"
              id="time"
              name="time"
              value={formData.time}
              onChange={handleInputChange}
              placeholder="e.g., 04:00 PM"
              className="custom-flyer-input custom-flyer-input-simple"
            />
          </div>

          {/* Location Input */}
          <div className="custom-flyer-field">
            <label htmlFor="location" className="custom-flyer-label-text">Location:</label>
            <input
              type="text"
              id="location"
              name="location"
              value={formData.location}
              onChange={handleInputChange}
              placeholder="Street address"
              className="custom-flyer-input custom-flyer-input-simple"
            />
          </div>

          {/* Address Input */}
          <div className="custom-flyer-field">
            <label htmlFor="address" className="custom-flyer-label-text">City, State & Zip:</label>
            <input
              type="text"
              id="address"
              name="address"
              value={formData.address}
              onChange={handleInputChange}
              placeholder="Columbus OH 43229"
              className="custom-flyer-input custom-flyer-input-simple"
            />
          </div>

          {/* Theme Input */}
          <div className="custom-flyer-field">
            <label htmlFor="theme" className="custom-flyer-label-text">Theme:</label>
            <textarea
              id="theme"
              name="theme"
              value={formData.theme}
              onChange={handleInputChange}
              placeholder="e.g., THE POWER OF FINANCIAL LITERACY"
              className="custom-flyer-textarea"
              rows="2"
            />
          </div>

          {/* Headshot Picture Upload */}
          <div className="custom-flyer-field">
            <label htmlFor="headshotPicture" className="custom-flyer-label-text">Headshot Picture:</label>
            <input
              type="file"
              id="headshotPicture"
              name="headshotPicture"
              onChange={handleFileChange}
              accept="image/*"
              className="custom-flyer-file-input"
            />
            {headshotPreview && (
              <div className="custom-flyer-preview">
                <img src={headshotPreview} alt="Headshot preview" className="custom-flyer-preview-img" />
              </div>
            )}
          </div>

          {/* Generate Button */}
          <button type="submit" className="custom-flyer-generate-btn">
            Generate & Download Flyer
          </button>
        </form>
      </div>
    </div>
  );
}

export default BusinessShowerFlyerModal;
